import {getCustomerAccount} from 'business/user'
import {PaymentFormValues} from 'components/PaymentForm/initialFormValues'
import {secureFieldsToBofhTypes} from 'configs/payment'
import {FormikProps} from 'formik'
import {
  __,
  add,
  assocPath,
  clone,
  dissoc,
  dissocPath,
  find,
  mergeDeepRight,
  path,
  pathOr,
  pick,
  pipe,
  propEq,
  split,
  zipObj
} from 'ramda'
import {logToDataDog} from 'services/datadogLogger'
import {getForterTokenObject} from 'services/forter'
import Psd2 from 'services/psd2'
import {ServerCode} from 'services/psd2/types'
import {stripe} from 'services/stripe'
import {SecureFieldsDataType} from 'types/payment'
import {isAlternativePaymentMethod} from 'utils/paymentMethodsHelpers'
import {urlParams} from 'utils/urlParams'

import {getBrandOverrideFromCookie} from '@vio/core/src/_web/brand/business/persistData'
import {PaymentMethodType} from '@vio/core/src/api-types/bovio/request/booking_creation'
import {Code as CardCode} from '@vio/core/src/api-types/bovio/response/payment_methods_settings'
import {
  BookingRequestWithPsd2,
  CreditCardPaymentRequest,
  PaymentInformationRequest,
  PhoneNumberRequest,
  RoomRequest
} from '@vio/core/src/booking/types/BookingRequest'
import {CountryType} from '@vio/core/src/localization/types/Country'
import {roomsSplitToString} from '@vio/core/src/room/business/roomConfiguration'
import {
  RoomConfigurationType,
  RoomSplitType
} from '@vio/core/src/room/types/RoomConfiguration'

import getRoomConfig from '../components/data/URLParams/mappers/getRoomConfig'

import type {User} from '@vio/core/src/auth/types/Cognito'

const pathToCreditCard = ['payment', 'creditCard']
const pathToRawExpirationDate = [...pathToCreditCard, 'rawExpirationDate']
const pathToExpirationYear = [...pathToCreditCard, 'expirationYear']
const pathToTransactionId = [...pathToCreditCard, 'transactionId']
const pathToStripeElementsPMId = [...pathToCreditCard, 'id']

const pathToBilling = ['payment', 'billing']
const pathToBillingCountryCode = [...pathToBilling, 'address', 'countryCode']
const expirationDateObjectKeys = ['expirationMonth', 'expirationYear']

export const getCustomerPhoneNumber = (formData: PaymentFormValues) =>
  formData?.customer?.phone

const getCustomerEmail = (formData: PaymentFormValues) =>
  formData?.customer?.email

const getRawExpirationDate = pathOr('', pathToRawExpirationDate)

const removeRawExpirationDate = dissocPath<PaymentFormValues>(
  pathToRawExpirationDate
)

const createExpirationDatesObject = zipObj(expirationDateObjectKeys)

const createCreditCardStructureAndUpdate = assocPath(pathToCreditCard, __, {})

const creatingBillingStructureAndUpdate = assocPath(pathToBilling, __, {})

export const breakCCExpirationDate = (
  formData: PaymentFormValues
): PaymentFormValues => {
  return pipe(
    getRawExpirationDate,
    split('/'),
    createExpirationDatesObject,
    createCreditCardStructureAndUpdate,
    mergeDeepRight(formData),
    removeRawExpirationDate,
    addTwoThousandYears
  )(formData)
}

const addTwoThousandYears = (formData: PaymentFormValues) => {
  const expirationYear = path<number>(pathToExpirationYear, formData)
  return assocPath(
    pathToExpirationYear,
    add(Number(expirationYear), 2000).toString(),
    formData
  )
}

export const transformDialCode = (
  roomPhoneNumber: PhoneNumberRequest,
  countriesList: CountryType[]
): PhoneNumberRequest => {
  const country: CountryType | undefined = find<CountryType>(
    propEq('code', roomPhoneNumber.countryCode),
    countriesList
  )
  return {
    ...roomPhoneNumber,
    countryCode: country ? country.dialCode : ''
  }
}

export const includeGuestsInRooms =
  (guestDistribution: readonly RoomSplitType[]) =>
  (formData: PaymentFormValues): PaymentFormValues => {
    const roomGuests = roomsSplitToString(guestDistribution).split('|')
    const rooms = formData.rooms.map((room, idx) => {
      room.guests = roomGuests[idx]
      return room
    })

    return {...formData, rooms}
  }

const includeRoomIdInRooms =
  (roomId: string) =>
  (formData: PaymentFormValues): PaymentFormValues => {
    const rooms = formData.rooms.map(room => {
      room.id = roomId
      return room
    })

    return {...formData, rooms}
  }

export const includeContactInfoIntoRooms =
  (roomPhoneNumber: PhoneNumberRequest, email: string) =>
  (formData: PaymentFormValues): PaymentFormValues => {
    const rooms = formData.rooms.map((room, index) => {
      // For single room: copy customer data
      // For multi-room: copy customer data to first room
      const shouldCopyCustomerToRoom = formData?.customer && index === 0

      if (shouldCopyCustomerToRoom) {
        room.firstName = formData.customer.firstName
        room.lastName = formData.customer.lastName
      }

      room.phone = roomPhoneNumber
      room.email = room.email ? room.email : email
      return room
    })
    return {...formData, rooms}
  }

const addTransactionId =
  (transactionId?: string) =>
  (formData: PaymentFormValues): PaymentFormValues => {
    return assocPath(pathToTransactionId, transactionId, formData)
  }

const addStripeElementsPMId =
  (stripeElementsPMId?: string) =>
  (formData: PaymentFormValues): PaymentFormValues => {
    if (!stripeElementsPMId) return formData
    return assocPath(pathToStripeElementsPMId, stripeElementsPMId, formData)
  }

/** Fields BoVio's `AssistedCreditCard` accepts (PCI Proxy tokenization). */
const assistedCardFields = [
  'expirationMonth',
  'expirationYear',
  'network',
  'transactionId',
  'type'
]

/** Fields BoVio's `StripeTokenizedCard` accepts. */
const stripeTokenizedCardFields = ['id']

/**
 * `payment.creditCard` doubles as Formik state, so it carries fields the API has
 * no schema for: `number` and `securityCode` exist only to give the hosted
 * iframes a path to hang validation errors on, and the PCI Proxy fields are
 * meaningless once Stripe owns the card. Both card schemas set
 * `additionalProperties: false`, so anything outside these lists fails the
 * `oneOf` on every branch.
 */
const pickContractCreditCardFields =
  (stripeElementsPMId?: string) =>
  (formData: PaymentFormValues): PaymentFormValues => {
    const creditCard = path<CreditCardPaymentRequest>(
      pathToCreditCard,
      formData
    )
    if (!creditCard) return formData

    const acceptedFields = stripeElementsPMId
      ? stripeTokenizedCardFields
      : assistedCardFields

    return assocPath(
      pathToCreditCard,
      pick(acceptedFields, creditCard),
      formData
    )
  }

/**
 * Falling through here leaves the country the form was seeded with from the
 * booker's location, which is the intended fallback when Stripe reports no card
 * country.
 */
const applyCardCountryToBilling =
  (cardCountry?: string) =>
  (formData: PaymentFormValues): PaymentFormValues => {
    if (!cardCountry) return formData
    return assocPath(pathToBillingCountryCode, cardCountry, formData)
  }

export const getConvertedCardType = (
  secureFieldsData: SecureFieldsDataType
): CardCode | null => {
  const cardType = secureFieldsData?.fields?.cardNumber?.paymentMethod
  const convertedCardType = cardType && secureFieldsToBofhTypes[cardType]
  if (convertedCardType) {
    return convertedCardType
  }

  return null
}

const splitFullName = (fullName = '') => {
  const [firstName, ...lastNames] = fullName.trim().split(' ')
  return {firstName, lastName: lastNames.join(' ')}
}

export const mapBillingName = (
  formData: PaymentFormValues
): PaymentFormValues => {
  const paymentMethodType = formData?.payment?.paymentMethodType

  if (isAlternativePaymentMethod(paymentMethodType)) {
    if (paymentMethodType === PaymentMethodType.Ideal) {
      const {firstName, lastName} = splitFullName(
        formData.payment.idealAccountHolderName
      )
      formData.payment.billing.firstName = firstName
      formData.payment.billing.lastName = lastName
      delete formData.payment.idealAccountHolderName
    } else {
      delete formData.payment.billing.firstName
      delete formData.payment.billing.lastName
    }
  } else {
    const ccData = formData?.payment?.creditCard
    const {firstName, lastName} = splitFullName(ccData?.cardholdersName)
    formData.payment.billing.firstName = firstName
    formData.payment.billing.lastName = lastName
    delete ccData?.cardholdersName
  }

  return formData
}

const formatBookerPhoneCountryCode =
  (countriesList: CountryType[]) => (formData: Readonly<PaymentFormValues>) => {
    if (formData.customer) {
      formData.customer.phone = transformDialCode(
        formData.customer.phone,
        countriesList
      )
    }

    return formData
  }

export const addBedTypeToSpecialRequests = (
  formData: Readonly<PaymentFormValues>
) => {
  const roomsWithBedConfiguration = formData.rooms.map(room => {
    const {bedType, specialRequest} = formData
    const smoking = formData.smoking || false

    const bedTypeRequest = bedType ? `My preferred bed type is ${bedType}` : ''

    const defaultSpecialRequest = bedTypeRequest || specialRequest || ''

    const specialRequestWithBedType =
      bedTypeRequest && specialRequest
        ? `${specialRequest} - ${bedTypeRequest}`
        : defaultSpecialRequest

    const editedRoom: RoomRequest = {
      ...room,
      specialRequest: specialRequestWithBedType,
      smoking
    }
    delete editedRoom.bedType

    return editedRoom
  })

  return {
    ...formData,
    rooms: roomsWithBedConfiguration
  }
}

interface GenerateBookingRequestProps {
  formData: PaymentFormValues
  roomsConfig: RoomConfigurationType
  countriesList: CountryType[]
  roomId: string
  transactionId?: string
  stripeElementsPMId?: string
  cardCountry?: string
  payment?: PaymentInformationRequest
}

const removeCreditCardForAlternativePayments = (
  formData: PaymentFormValues
): PaymentFormValues => {
  const paymentMethodType = formData?.payment?.paymentMethodType

  if (paymentMethodType && isAlternativePaymentMethod(paymentMethodType)) {
    delete formData.payment.creditCard
  }

  return formData
}

/**
 * BoVio's `SavedCardPayment` branch takes `useSaved: true` and no card: the
 * stored payment method already lives on the booking, so `bookPSD2` skips
 * tokenization and neither a PCI Proxy transaction nor a Stripe payment method
 * id exists to send. The branch stops requiring `creditCard`, but it still
 * validates the property when present, so the partial card the form carries
 * fails the request unless we drop it.
 */
const removeCreditCardForSavedCard = (
  formData: PaymentFormValues
): PaymentFormValues => {
  if (formData?.payment?.useSaved) {
    delete formData.payment.creditCard
  }

  return formData
}

const removeAddressForAlternativePayments = (
  formData: PaymentFormValues
): PaymentFormValues => {
  const paymentMethodType = formData?.payment?.paymentMethodType

  if (paymentMethodType && isAlternativePaymentMethod(paymentMethodType)) {
    delete formData.payment.billing.address
  }

  return formData
}

const removeFormOnlyFields = (formData: PaymentFormValues) =>
  pipe(
    dissoc('specialRequest'),
    dissoc('smoking'),
    dissoc('bedType'),
    dissoc('paymentType')
  )(formData)

export const generateBookingRequest = ({
  formData,
  roomsConfig,
  countriesList,
  roomId,
  transactionId,
  stripeElementsPMId,
  cardCountry,
  payment
}: GenerateBookingRequestProps): PaymentFormValues => {
  const formDataCopy = clone(formData)
  const transformedRoomPhoneNumber = transformDialCode(
    getCustomerPhoneNumber(formDataCopy),
    countriesList
  )
  const email = getCustomerEmail(formDataCopy)

  let result = pipe(
    includeContactInfoIntoRooms(transformedRoomPhoneNumber, email),
    includeGuestsInRooms(roomsConfig.roomsSplit),
    includeRoomIdInRooms(roomId),
    formatBookerPhoneCountryCode(countriesList),
    addBedTypeToSpecialRequests,
    removeFormOnlyFields
  )(formDataCopy) as PaymentFormValues

  if (payment) {
    // Wallet path (Apple Pay / Google Pay): we receive a fully formed payment object from the frontend, so we can skip all the transformations and just merge it in
    result = {...result, payment}
  } else {
    // Card+APM path: we need to transform the form data to match the expected structure of the booking creation endpoint

    // On the Stripe tokenization path, Stripe owns the raw card details
    // (including MM/YY) and we only send the Stripe Elements PM id. The Formik
    // `rawExpirationDate` holds a sentinel placeholder, so we skip the
    // split/zip pipeline and drop the field instead.
    const formatCCExpiratonDate = stripeElementsPMId
      ? removeRawExpirationDate
      : breakCCExpirationDate

    result = pipe(
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      _ => zipObj(['phone', 'email'], [transformedRoomPhoneNumber, email]),
      creatingBillingStructureAndUpdate,
      mergeDeepRight(result),
      mapBillingName,
      formatCCExpiratonDate,
      addTransactionId(transactionId),
      addStripeElementsPMId(stripeElementsPMId),
      pickContractCreditCardFields(stripeElementsPMId),
      applyCardCountryToBilling(cardCountry),
      removeCreditCardForAlternativePayments,
      removeCreditCardForSavedCard,
      removeAddressForAlternativePayments
    )(result) as PaymentFormValues
  }

  return result
}

const getExtraBookingCreationRequestParams = async ({
  serverCode
}: {
  serverCode: ServerCode
}) => {
  const Psd2Provider = Psd2.getProvider(serverCode)
  const threeDomainSecureParams = Psd2Provider
    ? await Psd2Provider.setup()
    : undefined
  return threeDomainSecureParams || {}
}

export const getRequestPayload = async ({
  formActions,
  serverCode,
  transactionId,
  stripeElementsPMId,
  cardCountry,
  user,
  isMemberPlus,
  countriesList,
  providerCode,
  payment
}: {
  formActions: FormikProps<PaymentFormValues>
  serverCode: ServerCode
  transactionId: string | undefined
  stripeElementsPMId?: string
  cardCountry?: string
  user: User | null
  isMemberPlus: boolean
  countriesList: CountryType[]
  providerCode: string | null | undefined
  payment?: PaymentInformationRequest
}): Promise<BookingRequestWithPsd2 | undefined> => {
  const params = urlParams()
  const {roomId} = params
  const roomsConfig = getRoomConfig(params)

  const formData = formActions.values
  const {
    payment: {paymentMethodType}
  } = formData

  if (roomId) {
    const customerAccount = getCustomerAccount(user, isMemberPlus)
    const brandCode = getBrandOverrideFromCookie() || undefined

    const threeDomainSecureParams = await getExtraBookingCreationRequestParams({
      serverCode
    })

    const payload = generateBookingRequest({
      formData,
      roomsConfig,
      countriesList,
      roomId,
      transactionId,
      stripeElementsPMId,
      cardCountry,
      payment
    })

    return {
      ...payload,
      brandCode,
      threeDomainSecureParams,
      payment: {
        ...payload.payment,
        ...getForterTokenObject(providerCode),
        ...stripe.getRadarSession(),
        paymentMethodType
      },
      customerAccount
    }
  }

  logToDataDog({
    logName: 'RequestPayloadBuild.Error',
    metadata: {roomId},
    level: 'error'
  })
}
